// Wallet — web port of the mobile WalletScreen. Three tabs:
//   Active   → claimed, non-expired coupons (show redemption code, mark used)
//   Used     → redeemed or expired coupons (dimmed)
//   Watching → deal watchlist (add items, re-arm/mute matches, delete)
// Token-styled for light + dark. No external QR lib on web, so the redemption
// code is shown prominently (cashier types it) — mobile shows a QR of the same
// code; both redeem via POST /api/wallet/<id>/redeem/.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Clock, X, Bookmark, Sparkles, Plus, Ticket,
} from 'lucide-react';
import {
  getWallet, redeemWalletCoupon, getWatchlist, addWatchlist,
  updateWatchlist, deleteWatchlist, discountLabel,
} from '../services/walletService';
import './WalletScreen.css';

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'used', label: 'Used' },
  { key: 'watching', label: 'Watching' },
];

function WalletScreen() {
  const [wallet, setWallet] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const [redeemFor, setRedeemFor] = useState(null);
  const [newItem, setNewItem] = useState({ item_text: '', store: '', target_price: '' });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [w, wl] = await Promise.allSettled([getWallet(), getWatchlist()]);
    if (w.status === 'fulfilled') setWallet(w.value);
    if (wl.status === 'fulfilled') setWatchlist(wl.value);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(
    () => wallet.filter((w) => w.status === 'claimed' && !w.coupon?.is_expired), [wallet]);
  const used = useMemo(
    () => wallet.filter((w) => w.status === 'redeemed' || w.coupon?.is_expired), [wallet]);
  const watching = useMemo(
    () => watchlist.filter((w) => w.status !== 'not_interested'), [watchlist]);

  const counts = { active: active.length, used: used.length, watching: watching.length };

  const onRedeemed = async () => { setRedeemFor(null); await load(); };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.item_text.trim()) return;
    setAdding(true);
    try {
      const entry = await addWatchlist({
        item_text: newItem.item_text.trim(),
        store: newItem.store.trim(),
        target_price: newItem.target_price.trim(),
      });
      setWatchlist((prev) => [entry, ...prev]);
      setNewItem({ item_text: '', store: '', target_price: '' });
    } finally {
      setAdding(false);
    }
  };

  const rearm = async (w) => {
    const entry = await updateWatchlist(w.id, { status: 'open' });
    setWatchlist((prev) => prev.map((x) => (x.id === w.id ? entry : x)));
  };
  const mute = async (w) => {
    await updateWatchlist(w.id, { status: 'not_interested' });
    setWatchlist((prev) => prev.filter((x) => x.id !== w.id));
  };
  const remove = async (w) => {
    await deleteWatchlist(w.id);
    setWatchlist((prev) => prev.filter((x) => x.id !== w.id));
  };

  return (
    <div className="wallet-screen">
      <div className="wallet-inner">
        <header className="wallet-head">
          <p className="wallet-sub">{counts.active} active coupon{counts.active === 1 ? '' : 's'}</p>
        </header>

        <div className="wallet-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`wallet-tab${tab === t.key ? ' active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {counts[t.key] > 0 && <span className="wallet-tab-count">{counts[t.key]}</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="wallet-empty">Loading wallet…</div>
        ) : tab === 'active' ? (
          active.length === 0 ? <Empty text="No active coupons. Claim some on the Deals tab." />
            : <div className="wallet-coupon-grid">
              {active.map((w) => <WalletCouponCard key={w.id} w={w} onShow={() => setRedeemFor(w)} />)}
            </div>
        ) : tab === 'used' ? (
          used.length === 0 ? <Empty text="No used or expired coupons yet." />
            : <div className="wallet-coupon-grid dim">
              {used.map((w) => <WalletCouponCard key={w.id} w={w} used />)}
            </div>
        ) : (
          <>
            <form className="watch-add" onSubmit={addItem}>
              <input
                className="watch-input grow"
                placeholder="Watch an item (e.g. olive oil)"
                value={newItem.item_text}
                onChange={(e) => setNewItem((s) => ({ ...s, item_text: e.target.value }))}
              />
              <input
                className="watch-input"
                placeholder="Store (optional)"
                value={newItem.store}
                onChange={(e) => setNewItem((s) => ({ ...s, store: e.target.value }))}
              />
              <input
                className="watch-input"
                placeholder="Target price"
                value={newItem.target_price}
                onChange={(e) => setNewItem((s) => ({ ...s, target_price: e.target.value }))}
              />
              <button className="watch-add-btn" disabled={adding || !newItem.item_text.trim()}>
                <Plus size={16} /> Watch
              </button>
            </form>

            {watching.length === 0 ? (
              <Empty text="Nothing on your watchlist yet. Add an item above to get notified when it goes on sale." />
            ) : (
              <div className="watch-list">
                {watching.map((w) => (
                  <WatchCard key={w.id} w={w} onRearm={() => rearm(w)} onMute={() => mute(w)} onRemove={() => remove(w)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {redeemFor && (
        <RedemptionModal w={redeemFor} onClose={() => setRedeemFor(null)} onRedeemed={onRedeemed} />
      )}
    </div>
  );
}

function Empty({ text }) {
  return <div className="wallet-empty"><Ticket size={26} /><p>{text}</p></div>;
}

function WalletCouponCard({ w, onShow, used }) {
  const c = w.coupon || {};
  const expired = c.is_expired;
  return (
    <article className={`wallet-coupon${used ? ' used' : ''}`}>
      <div className="coupon-top">
        <span className="coupon-badge">{discountLabel(c)}</span>
        <span className="coupon-store">{c.store_name}</span>
      </div>
      <h3 className="coupon-title">{c.title}</h3>
      <div className="coupon-foot">
        <span className="coupon-expiry">
          <Clock size={13} />{' '}
          {w.status === 'redeemed' ? 'Used' : expired ? 'Expired' : `${c.days_left}d left`}
        </span>
        {!used && (
          <button className="coupon-claim" onClick={onShow}>Show code</button>
        )}
      </div>
    </article>
  );
}

function WatchCard({ w, onRearm, onMute, onRemove }) {
  const matched = w.status === 'matched' && w.matched;
  return (
    <article className={`watch-card${matched ? ' matched' : ''}`}>
      <div className="watch-main">
        <span className="watch-icon">{matched ? <Sparkles size={16} /> : <Bookmark size={16} />}</span>
        <div className="watch-text">
          <span className="watch-item">{w.item_text}</span>
          <span className="watch-meta">
            {w.store ? `at ${w.store}` : ''}{w.store && w.target_price ? ' · ' : ''}
            {w.target_price ? `target ${w.target_price}` : ''}
          </span>
        </div>
        <button className="watch-x" onClick={onRemove} aria-label="Remove"><X size={16} /></button>
      </div>
      {matched && (
        <div className="watch-match">
          <span className="watch-match-text">
            💸 {w.matched.item_name} · {w.matched.price} · {w.matched.store}
          </span>
          <div className="watch-match-actions">
            <button onClick={onRearm}>Keep watching</button>
            <button className="muted" onClick={onMute}>Not interested</button>
          </div>
        </div>
      )}
    </article>
  );
}

function RedemptionModal({ w, onClose, onRedeemed }) {
  const c = w.coupon || {};
  const [redeeming, setRedeeming] = useState(false);
  const redeem = async () => {
    if (!window.confirm('Did the cashier accept this coupon? This marks it as used.')) return;
    setRedeeming(true);
    try {
      await redeemWalletCoupon(w.id);
      onRedeemed();
    } catch (_) {
      window.alert('Could not redeem. Please try again.');
      setRedeeming(false);
    }
  };
  return (
    <div className="redeem-overlay" onClick={onClose}>
      <div className="redeem-modal" onClick={(e) => e.stopPropagation()}>
        <div className="redeem-head">
          <div>
            <span className="redeem-store">{c.store_name}</span>
            <h2 className="redeem-title">{c.title}</h2>
          </div>
          <button className="redeem-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="redeem-badge">{discountLabel(c)}</div>
        <div className="redeem-code-box">
          <span className="redeem-code-label">Redemption code</span>
          <span className="redeem-code">{w.redemption_code}</span>
          <span className="redeem-code-hint">Show this code to the cashier</span>
        </div>
        {c.valid_until && (
          <div className="redeem-valid"><Clock size={13} /> Valid until {c.valid_until}</div>
        )}
        <button className="redeem-btn" disabled={redeeming} onClick={redeem}>
          {redeeming ? 'Marking…' : 'Mark as used'}
        </button>
      </div>
    </div>
  );
}

export default WalletScreen;
